import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { PaEmptyState } from "components/platform-admin";
import {
  buildCollectionsRows,
  formatBillingDate,
  formatCurrency,
} from "./billingOverview.utils";
import FinancialQueueActionButtons from "./FinancialQueueActionButtons";
import { buildCollectionsRowActions } from "./financialQueueActions.utils";

const FinancialCollectionsPanel = ({
  collections = [],
  summary = null,
  permissions = {},
  onAction = null,
}) => {
  const rows = useMemo(() => buildCollectionsRows(collections), [collections]);
  const currency = summary?.currency || rows[0]?.currency || "AUD";

  if (rows.length === 0) {
    return (
      <PaEmptyState
        title="Collections queue clear"
        message="No tenants with open or overdue invoices in synced Stripe data."
        description="Run Sync Stripe invoices on the financial overview if amounts look stale."
      />
    );
  }

  const renderActions = (row) => {
    if (!onAction) {
      return null;
    }

    return (
      <FinancialQueueActionButtons
        actions={buildCollectionsRowActions(row, permissions)}
        onAction={onAction}
        row={row}
        layout="stack"
      />
    );
  };

  return (
    <section className="space-y-4">
      {summary ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Tenants</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{summary.tenant_count ?? 0}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Outstanding</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(summary.outstanding_cents, currency)}
            </p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-red-700">Overdue</p>
            <p className="mt-1 text-lg font-semibold text-red-900">
              {formatCurrency(summary.overdue_cents, currency)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Overdue invoices
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {summary.overdue_invoice_count ?? 0}
            </p>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Tenant</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Outstanding</th>
              <th className="px-4 py-2.5">Overdue</th>
              <th className="px-4 py-2.5">Oldest overdue</th>
              {onAction ? <th className="min-w-[10.5rem] px-4 py-2.5">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50/80">
                <td className="px-4 py-3">
                  <Link
                    to={`/tenants/${row.tenantId}?tab=billing`}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {row.name}
                  </Link>
                  {row.plan_name ? (
                    <p className="mt-0.5 text-xs text-gray-500">{row.plan_name}</p>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-xs capitalize text-gray-600">
                  {row.subscription_current_status?.replace(/_/g, " ") || "—"}
                  {row.read_only ? (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                      Read-only
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-xs text-gray-700">
                  {formatCurrency(row.outstanding_cents, row.currency || currency)}
                  {row.open_invoice_count ? (
                    <span className="text-gray-500"> · {row.open_invoice_count} open</span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-xs font-medium text-red-700">
                  {row.overdue_cents > 0
                    ? `${formatCurrency(row.overdue_cents, row.currency || currency)} (${row.overdue_invoice_count})`
                    : "—"}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {row.oldest_overdue_at ? formatBillingDate(row.oldest_overdue_at) : "—"}
                </td>
                {onAction ? (
                  <td className="px-4 py-3 align-top">{renderActions(row)}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default FinancialCollectionsPanel;
